import {Component, Input, OnInit, SimpleChanges, OnChanges, OnDestroy} from '@angular/core';
import createAIFinalizeSteps from './create-ai-assistant-finalize.json';
import createAIFinalizeBlogSteps from './create-ai-assistant-finalize-blog.json';
import {DbModalService} from "../../../../../../modules/modal/modal";
import {GoogleAnalyticsService} from "../../../../../../shared/services/google-analytics/google-analytics.service";
import * as fromStore from "../../../../../../store";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {Store} from "@ngrx/store";
import {UserService} from "../../../../../../user/user.service";
import {CookieService} from "ngx-cookie";
import {TypeUtils} from "../../../../../../shared/utils/type-utils";
import {combineLatest, Subject} from "rxjs";

type createAIFinalizeStepStatus = 'todo' | 'process' | 'passed';

interface ICreateAIFinalizeStep {
  label: string;
  status: createAIFinalizeStepStatus | string;
  isEcommerce?: boolean;
}

@Component({
    selector: 'db-create-ai-assistant-finalize',
    templateUrl: './create-ai-assistant-finalize.component.html',
    styleUrls: ['./create-ai-assistant-finalize.component.scss'],
    standalone: false
})
export class CreateAiAssistantFinalizeComponent implements OnInit, OnChanges, OnDestroy {

  @Input() recreationFinished: boolean;
  @Input() isEcommerce: boolean;
  @Input() blogType: boolean;
  public steps: ICreateAIFinalizeStep[];
  public isMobile: boolean;
  public userLocalStorage: any;
  public updateLocalStorage: boolean = false;
  private counter: number = -1;
  public countryName: string = '';
  public companyName: string = 'Dashboard';
  private interval: ReturnType<typeof setInterval>;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _userService: UserService,
    private _cookieService: CookieService,
    private _dbModalService: DbModalService,
    private _store: Store<fromStore.DbStoreState>,
    private _googleAnalyticsService: GoogleAnalyticsService,
  ) {
  }

  ngOnInit() {
    const _steps = !!this.blogType ? createAIFinalizeBlogSteps : createAIFinalizeSteps;
    this.steps = _steps.filter(step => step['isEcommerce'] === this.isEcommerce || step['isEcommerce'] === undefined);
    localStorage.setItem('showFinalize', '1');
    if (window.innerWidth < 991) {
      this.isMobile = true;
    }
    !this.isMobile && this._getUserLocalStorage();
    this.counter = -1;
    this._initSteps();
    this._toNextStep(6000);
    /* after 5mins if there is no changes remove showfinalize item */
    setTimeout( () => {
      localStorage.removeItem('showFinalize');
    }, 300000);
  }

  private _getUserLocalStorage(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUserLocalStorage),
          this._store.select(fromStore.getUserMeta()),
        )),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([userLocalStorage, meta]) => {

        this.userLocalStorage = userLocalStorage;
        !this.updateLocalStorage && this._updateUserLocalStorage();

        const getExperimentalCookie = this._cookieService.get('db-experiment');
        const isTier1Country = getExperimentalCookie ? JSON.parse(getExperimentalCookie) && JSON.parse(getExperimentalCookie).t1PExp && JSON.parse(getExperimentalCookie).t1PExp === 'b' : false;
        if (TypeUtils.isDefined(meta) && TypeUtils.isDefined(meta['country_code'])) {
          this.countryName = TypeUtils.isDefined(meta['country_code'][0]) ? meta['country_code'][0] : '';
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.recreationFinished = changes['recreationFinished'].currentValue;
    if (this.recreationFinished) {
      this._dbModalService.dismissAll();
      clearInterval(this.interval);
      this._toNextStep(200);
    }
  }

  ngOnDestroy() {
    clearInterval(this.interval);
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private _initSteps() {
    this.steps.map((step, index) => {
      step.status = index === 0 ? "process" : "todo";
    });
  }

  private _checkSuggestedDomain(domainName) {
    this.userLocalStorage['suggestedDomainNameInitial'] = localStorage.getItem('dbOnboardingDomainNameInitial');
    this.userLocalStorage['suggestedDomainName'] = domainName;
    this._updateLocalStorage();
  }

  private _updateUserLocalStorage(updateDiscovery: boolean = false): void {
    !!updateDiscovery && (this.userLocalStorage['showDiscoveryDialog'] = true);
    if ( localStorage.getItem('dbOnboardingDomainName') ) {
      this._checkSuggestedDomain(localStorage.getItem('dbOnboardingDomainName'));
      localStorage.removeItem('dbOnboardingDomainName');
      localStorage.removeItem('dbOnboardingDomainNameInitial');
      localStorage.removeItem('dbOnboardingDomainInfo');
    }
    else this._updateLocalStorage();
  }

  private _updateLocalStorage(): void {
    this._userService.setToLocal(this.userLocalStorage)
        .pipe(
            first(response => !!response, null),
        )
        .subscribe(response => {
          if (response.success) {
            this.updateLocalStorage = true;
            this._store.dispatch(new fromStore.LoadUserLocalStorage());
            this._store.dispatch(new fromStore.LoadUser());
          }
        });
  }

  private _sendGaEvent(action): void {
    const analyticsData = {
      eventCategory: 'Dashboard Action',
      eventAction: action,
      label: ''
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  private _toNextStep(time = 5000): void {
    this.interval = setInterval(() => {
      this.counter++;
      if (this.counter > this.steps.length - 1) this.counter = this.steps.length - 1;
      if (this.counter !== this.steps.length - 1 || this.recreationFinished) {
        this.steps[this.counter].status = "passed";
      }
      if (this.counter === this.steps.length - 1) {
        clearInterval(this.interval);
        return;
      }
      this.steps[this.counter + 1].status = "process" ;
    }, time);
  }

  private _getCompanyInfo(): void {
    this._store.select(fromStore.getCompanyInfoLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getCompanyInfo)),
        first()
      )
      .subscribe((companyInfo: any) => {
        this.companyName = companyInfo?.branding?.company_name || companyInfo?.agency?.name || 'YourBrand';
        // Update the first step label with the company name
        if (this.steps && this.steps.length > 0) {
          this.steps[0].label = `Creating a blank website on ${this.companyName} Hosting`;
        }
      });
  }

}
